import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { Check, X, Star, Users, Crown, Shield, Clock, Phone, Eye, Heart, ArrowRight, Calculator, TrendingDown, Sparkles, AlertTriangle, TrendingUp, Calendar } from 'lucide-react';

// Messaggi stagionali per ogni mese
const SEASONAL_MESSAGES = {
  0: { // Gennaio
    title: "Nuovo anno, nuova visione",
    message: "Gennaio è il momento perfetto per iniziare a prenderti cura della tua vista. Inizia l'anno con un check-up completo.",
    icon: "🎯"
  },
  1: { // Febbraio
    title: "L'amore per i tuoi occhi",
    message: "Febbraio: prenditi cura di te stesso. La vista è il senso più prezioso, non trascurarla.",
    icon: "❤️"
  },
  2: { // Marzo
    title: "Primavera in arrivo",
    message: "Con l'arrivo della primavera, allergie e pollini possono irritare gli occhi. Preparati con un controllo preventivo.",
    icon: "🌸"
  },
  3: { // Aprile
    title: "Stagione delle allergie",
    message: "Aprile porta sole e allergie. Proteggi i tuoi occhi con un piano di prevenzione su misura.",
    icon: "🌼"
  },
  4: { // Maggio
    title: "Prepara l'estate",
    message: "Maggio: è tempo di preparare i tuoi occhi per l'estate. Sole intenso e mare richiedono protezione.",
    icon: "☀️"
  },
  5: { // Giugno
    title: "Estate alle porte",
    message: "L'estate è vicina: raggi UV più intensi, aria condizionata, piscine. I tuoi occhi hanno bisogno di attenzione.",
    icon: "🏖️"
  },
  6: { // Luglio
    title: "Protezione estiva",
    message: "Luglio: il sole è al massimo. Non sottovalutare i danni da esposizione prolungata ai raggi UV.",
    icon: "😎"
  },
  7: { // Agosto
    title: "Vacanze e prevenzione",
    message: "Agosto: tra mare e montagna, i tuoi occhi sono esposti. Un abbonamento ti garantisce assistenza ovunque.",
    icon: "🌊"
  },
  8: { // Settembre
    title: "Ritorno alla routine",
    message: "Settembre: si torna a scuola e al lavoro. Un controllo visivo è fondamentale per la concentrazione.",
    icon: "📚"
  },
  9: { // Ottobre
    title: "Prevenzione autunnale",
    message: "Ottobre: le giornate si accorciano. Affaticamento visivo e luce artificiale aumentano. Prenditi cura dei tuoi occhi.",
    icon: "🍂"
  },
  10: { // Novembre
    title: "Mese della prevenzione",
    message: "Novembre è il mese della prevenzione. Il 40% dei problemi visivi si può prevenire con controlli regolari.",
    icon: "🔬"
  },
  11: { // Dicembre
    title: "Un regalo per la tua vista",
    message: "Dicembre: regala salute a te e alla tua famiglia. Un abbonamento è il dono più prezioso.",
    icon: "🎁"
  }
};

// Statistiche social proof (simulate ma realistiche)
const generateSocialProofStats = () => {
  const currentMonth = new Date().getMonth();
  const baseSubscribers = 847; // Base di partenza
  const monthlyGrowth = [12, 15, 18, 22, 19, 25, 28, 24, 31, 27, 35, 42]; // Crescita mensile tipica
  const currentMonthSubscribers = monthlyGrowth[currentMonth] + Math.floor(Math.random() * 8);
  
  return {
    totalFamilies: baseSubscribers + (currentMonth * 20) + Math.floor(Math.random() * 30),
    thisMonth: currentMonthSubscribers,
    lastWeek: Math.floor(currentMonthSubscribers / 4) + Math.floor(Math.random() * 3)
  };
};

// Prezzi singole prestazioni (per il calcolatore) - dal listino ufficiale
const SINGLE_PRICES = {
  topografia: 90,
  retinografia: 100,
  fondo_oculare: 90,
  oct: 100,
  campo_visivo: 90,
  ecografia: 120,
  conta_endoteliale: 100,
  visita_ortottica: 100,
  trattamento_luteina: 180,
  luce_pulsata: 180,
  laser_slt: 700,
  iridotomia: 450,
  capsulotomia: 350,
  cataratta: 3000,
  strabismo: 4250,
  accompagnamento: 120,
  farmaci_colliri: 115
};

const PLANS = {
  base: {
    name: 'Base',
    color: '#2A9FCB',
    colorLight: '#E8F5E9',
    icon: Shield,
    tagline: 'Per chi vuole essere seguito',
    // Prezzi mensili per durata: 1 anno, 3 anni, 5 anni
    prices: { 
      individual: { 1: 59, 3: 49, 5: 39 }, 
      family: { 1: 179, 3: 159, 5: 139 } 
    },
    features: {
      diagnostica_completa: true, // 
      visite_controlli: true,
      followup: true,
      oct_incluso: true,
      campo_visivo: true,
      topografia: true,
      retinografia: true,
      ecografia: true,
      trattamenti_avanzati: false, // 
      farmaci_colliri: false,
      medico_online: false,
      segreteria_h24: false,
      accompagnamento: false,
      chirurgia_inclusa: false
    },
    included: [
      'Tutte le prestazioni diagnostiche ',
      'Topografia corneale',
      'Retinografia',
      'Fondo oculare',
      'OCT',
      'Campo visivo',
      'Ecografia bulbare',
      'Conta endoteliale',
      'Visita ortottica',
      'Visite di controllo illimitate',
      'Follow-up inclusi'
    ],
    excluded: [
      'Trattamenti avanzati ',
      'Farmaci e colliri',
      'Medico personale online',
      'Segreteria dedicata h24',
      'Servizio accompagnamento'
    ]
  },
  premium: {
    name: 'Premium',
    color: '#D4AF37',
    colorLight: '#FEF9E7',
    icon: Crown,
    tagline: 'La scelta completa ⭐',
    // Prezzi mensili per durata: 1 anno, 3 anni, 5 anni
    prices: { 
      individual: { 1: 159, 3: 149, 5: 139 }, 
      family: { 1: 499, 3: 389, 5: 369 } 
    },
    recommended: true,
    features: {
      diagnostica_completa: true, // 
      visite_controlli: true,
      followup: true,
      oct_incluso: true,
      campo_visivo: true,
      topografia: true,
      retinografia: true,
      ecografia: true,
      trattamenti_avanzati: true, // 
      farmaci_colliri: true,
      medico_online: true,
      segreteria_h24: true,
      accompagnamento: true,
      chirurgia_inclusa: true
    },
    included: [
      'Tutto del piano Base +',
      'Tutti i trattamenti avanzati ',
      'Trattamento con luteina',
      'Luce pulsata / LLLT',
      'Laser SLT',
      'Iridotomia laser',
      'Capsulotomia YAG laser',
      'Intervento di cataratta incluso',
      'Intervento di strabismo incluso',
      'Farmaci e colliri inclusi',
      'Medico personale online',
      'Segreteria dedicata h24',
      'Servizio presa e riaccompagnamento'
    ],
    excluded: []
  }
};

const COMPARISON_FEATURES = [
  { key: 'diagnostica_completa', label: 'Prestazioni diagnostiche ', type: 'boolean' },
  { key: 'visite_controlli', label: 'Visite e controlli illimitati', type: 'boolean' },
  { key: 'followup', label: 'Follow-up inclusi', type: 'boolean' },
  { key: 'oct_incluso', label: 'OCT', type: 'boolean' },
  { key: 'campo_visivo', label: 'Campo visivo', type: 'boolean' },
  { key: 'topografia', label: 'Topografia corneale', type: 'boolean' },
  { key: 'retinografia', label: 'Retinografia', type: 'boolean' },
  { key: 'ecografia', label: 'Ecografia bulbare', type: 'boolean' },
  { key: 'trattamenti_avanzati', label: 'Trattamenti avanzati ', type: 'boolean' },
  { key: 'farmaci_colliri', label: 'Farmaci e colliri inclusi', type: 'boolean' },
  { key: 'medico_online', label: 'Medico personale online', type: 'boolean' },
  { key: 'segreteria_h24', label: 'Segreteria dedicata h24', type: 'boolean' },
  { key: 'accompagnamento', label: 'Servizio accompagnamento', type: 'boolean' },
  { key: 'chirurgia_inclusa', label: 'Interventi chirurgici inclusi', type: 'boolean' }
];

export const Abbonamenti = () => {
  const [billingType, setBillingType] = useState('individual');
  const [duration, setDuration] = useState(1); // 1, 3, o 5 anni
  const navigate = useNavigate();
  
  // Social proof and seasonal
  const [socialStats, setSocialStats] = useState(generateSocialProofStats());
  const [currentMonth] = useState(new Date().getMonth());
  const seasonalMessage = SEASONAL_MESSAGES[currentMonth];
  
  // Aggiorna stats ogni 30 secondi per dare senso di movimento
  useEffect(() => {
    const interval = setInterval(() => {
      setSocialStats(prev => ({
        ...prev,
        thisMonth: prev.thisMonth + (Math.random() > 0.7 ? 1 : 0),
        lastWeek: prev.lastWeek + (Math.random() > 0.8 ? 1 : 0)
      }));
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Calculator state
  const [calcVisits, setCalcVisits] = useState(2);
  const [calcControls, setCalcControls] = useState(2);
  const [calcOCT, setCalcOCT] = useState(1);
  const [calcCampoVisivo, setCalcCampoVisivo] = useState(1);
  const [calcTopografia, setCalcTopografia] = useState(1);
  const [calcTonometria, setCalcTonometria] = useState(1);
  const [calcRetinografia, setCalcRetinografia] = useState(1);
  const [calcEcografia, setCalcEcografia] = useState(0);
  const [calcChirurgia, setCalcChirurgia] = useState(false);

  // Calculate single visit costs
  const calculateSingleCost = () => {
    let total = 0;
    total += calcVisits * 150; // Visita oculistica completa
    total += calcControls * 100; // Visita di controllo
    total += calcOCT * 100; // OCT
    total += calcCampoVisivo * 90; // Campo visivo
    total += calcTopografia * 90; // Topografia corneale
    total += calcTonometria * 50; // Tonometria
    total += calcRetinografia * 100; // Retinografia
    total += calcEcografia * 120; // Ecografia bulbare
    if (calcChirurgia) total += 3000; // Intervento chirurgico
    return total;
  };

  const singleCost = calculateSingleCost();
  
  // Helper per ottenere il prezzo mensile
  const getMonthlyPrice = (plan, type, dur) => {
    return plan.prices[type][dur];
  };
  
  // Helper per calcolare il costo annuale abbonamento
  const getAnnualCost = (plan, type, dur) => {
    return plan.prices[type][dur] * 12;
  };
  
  // Calculate savings for each plan
  const calculateSavings = (planKey) => {
    const plan = PLANS[planKey];
    const annualCost = getAnnualCost(plan, billingType, duration);
    
    // Per entrambi i piani, tutte le prestazioni diagnostiche sono incluse
    let coveredValue = 0;
    
    // Visite e controlli (inclusi in entrambi)
    coveredValue += calcVisits * 150;
    coveredValue += calcControls * 100;
    
    // Diagnostica (inclusa in entrambi)
    coveredValue += calcOCT * 100;
    coveredValue += calcCampoVisivo * 90;
    coveredValue += calcTopografia * 90;
    coveredValue += calcTonometria * 50;
    coveredValue += calcRetinografia * 100;
    coveredValue += calcEcografia * 120;
    
    // Se l'utente seleziona chirurgia ma il piano non la include, 
    // il costo non viene coperto e rimane una spesa extra
    let extraCost = 0;
    if (calcChirurgia) {
      if (planKey === 'premium') {
        // Premium include chirurgia, aggiungiamo al valore coperto
        coveredValue += 3000;
      } else {
        // Base NON include chirurgia, l'utente deve pagarla extra
        extraCost = 3000;
      }
    }
    
    // Risparmio = valore coperto - costo abbonamento - eventuali extra
    const savings = coveredValue - annualCost - extraCost;
    const percentage = coveredValue > 0 ? Math.round((savings / (coveredValue + extraCost)) * 100) : 0;
    
    return { savings, percentage, coveredValue, annualCost, extraCost };
  };

  const handleSubscribe = (planKey) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }
    navigate(`/checkout/${planKey}-${billingType}`);
  };

  const renderFeatureValue = (feature, value) => {
    switch (feature.type) {
      case 'boolean':
        return value ? (
          <Check className="w-5 h-5 mx-auto" style={{color: '#10B981'}} />
        ) : (
          <X className="w-5 h-5 mx-auto" style={{color: '#EF4444'}} />
        );
      case 'number':
        return <span className="font-semibold">{value === 'illimitate' || value === 'illimitati' ? '∞' : value}</span>;
      case 'percent':
        return <span className="font-semibold">{value === 100 ? 'Incluse' : `${value}%`}</span>;
      case 'surgery':
        return <span className="font-semibold">{value === 0 ? '-' : value}</span>;
      default:
        return value;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Urgency Banner - Top - Luxury */}
      <div className="pt-24 px-6" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
        <div className="max-w-5xl mx-auto py-4 text-center">
          <div className="inline-flex flex-wrap items-center justify-center gap-3 px-6 py-3 rounded-full bg-amber-50 border border-amber-200">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <span className="text-[#1E3A5F]">
              Il <strong>40% dei problemi visivi</strong> si può prevenire con controlli regolari.
            </span>
          </div>
        </div>
      </div>
      
      {/* Hero Section - Luxury */}
      <section className="pt-8 pb-16 px-6 md:px-12" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
        <div className="max-w-5xl mx-auto text-center">
          {/* Seasonal Message */}
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full mb-8 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.05)] border border-gray-100">
            <span className="text-2xl">{seasonalMessage.icon}</span>
            <div className="text-left">
              <p className="font-medium text-sm" style={{color: '#1A1A1A'}}>{seasonalMessage.title}</p>
              <p className="text-xs" style={{color: '#666666'}}>{seasonalMessage.message}</p>
            </div>
          </div>
          
          <p 
            className="text-sm font-medium tracking-[0.3em] uppercase mb-4"
            style={{ color: '#2A9FCB' }}
          >
            Piani di Prevenzione
          </p>
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl font-normal mb-6 tracking-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#1A1A1A' }}
          >
            I Nostri Abbonamenti
          </h1>
          <p className="text-lg md:text-xl mb-4" style={{color: '#666666'}}>
            Un nuovo modo di prendersi cura della vista
          </p>
          <p className="text-base max-w-3xl mx-auto mb-8" style={{color: '#666666'}}>
            Meno pensieri, più valore, più continuità. Risparmia fino al <strong style={{color: '#2A9FCB'}}>60%</strong> rispetto alle singole prestazioni.
          </p>
          
          {/* Social Proof Stats */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            <div className="flex items-center gap-2 px-5 py-2 rounded-full bg-white border border-gray-200">
              <Users className="w-5 h-5" style={{color: '#2A9FCB'}} />
              <span className="text-sm font-medium" style={{color: '#1A1A1A'}}>
                Chi ci ha scelto non ha più cambiato
              </span>
            </div>
            <div className="flex items-center gap-2 px-5 py-2 rounded-full bg-white border border-gray-200">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium" style={{color: '#1A1A1A'}}>
                +{socialStats.thisMonth} nuovi iscritti questo mese
              </span>
            </div>
          </div>
          
          {/* Billing Toggle - Luxury */}
          <div className="inline-flex rounded-full p-1.5 bg-gray-100">
            <button
              onClick={() => setBillingType('individual')}
              className={`px-8 py-3 rounded-full font-medium text-sm transition-all ${billingType === 'individual' ? 'shadow-md' : ''}`}
              style={{
                background: billingType === 'individual' ? '#2A9FCB' : 'transparent',
                color: billingType === 'individual' ? 'white' : '#666666'
              }}
              data-testid="toggle-individual"
            >
              Individuale
            </button>
            <button
              onClick={() => setBillingType('family')}
              className={`px-8 py-3 rounded-full font-medium text-sm transition-all ${billingType === 'family' ? 'shadow-md' : ''}`}
              style={{
                background: billingType === 'family' ? '#2A9FCB' : 'transparent',
                color: billingType === 'family' ? 'white' : '#666666'
              }}
              data-testid="toggle-family"
            >
              👨‍👩‍👧‍👦 Famiglia (max 5)
            </button>
          </div>
          
          {/* Duration Toggle */}
          <div className="mt-4">
            <p className="text-sm text-[#2A9FCB]/70 mb-2">Durata abbonamento:</p>
            <div className="inline-flex rounded-xl p-1.5 shadow-lg" style={{background: 'white'}}>
              <button
                onClick={() => setDuration(1)}
                className={`px-6 py-2 rounded-lg font-semibold transition-all text-sm ${duration === 1 ? 'shadow-md' : ''}`}
                style={{
                  background: duration === 1 ? '#2A9FCB' : 'transparent',
                  color: duration === 1 ? 'white' : '#2A9FCB'
                }}
                data-testid="duration-1"
              >
                1 Anno
              </button>
              <button
                onClick={() => setDuration(3)}
                className={`px-6 py-2 rounded-lg font-semibold transition-all text-sm ${duration === 3 ? 'shadow-md' : ''}`}
                style={{
                  background: duration === 3 ? '#F59E0B' : 'transparent',
                  color: duration === 3 ? 'white' : '#D97706'
                }}
                data-testid="duration-3"
              >
                3 Anni
              </button>
              <button
                onClick={() => setDuration(5)}
                className={`px-6 py-2 rounded-lg font-semibold transition-all text-sm ${duration === 5 ? 'shadow-md' : ''}`}
                style={{
                  background: duration === 5 ? '#8B5CF6' : 'transparent',
                  color: duration === 5 ? 'white' : '#6D28D9'
                }}
                data-testid="duration-5"
              >
                5 Anni
              </button>
            </div>
            {duration > 1 && (
              <p className="text-xs mt-2" style={{color: duration === 3 ? '#D97706' : '#6D28D9'}}>
                ✨ Risparmio garantito per {duration} anni!
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Pricing Cards - 2 Plans: Base and Premium */}
      <section className="py-12 px-4 -mt-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {Object.entries(PLANS).map(([key, plan], index) => {
              const Icon = plan.icon;
              const isRecommended = plan.recommended;
              
              return (
                <div 
                  key={key}
                  data-testid={`plan-card-${key}`}
                  className={`relative bg-white rounded-2xl overflow-hidden ${isRecommended ? 'shadow-2xl ring-4' : 'shadow-lg'}`}
                  style={{
                    '--tw-ring-color': isRecommended ? '#D4AF37' : 'transparent'
                  }}
                >
                  {/* Recommended Badge */}
                  {isRecommended && (
                    <div className="absolute -top-0 left-0 right-0 py-2 text-center text-sm font-bold text-white" style={{background: '#D4AF37'}}>
                      ⭐ CONSIGLIATO
                    </div>
                  )}
                  
                  {/* Header */}
                  <div className={`p-6 text-center ${isRecommended ? 'pt-12' : ''}`} style={{background: plan.colorLight}}>
                    <div className="w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center" style={{background: plan.color}}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold" style={{color: plan.color}}>
                      {plan.name}
                    </h3>
                    <p className="text-sm mt-1 text-[#2A9FCB]/70">
                      {plan.tagline}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="p-6 text-center border-b border-[#2A9FCB]/10">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-5xl font-bold" style={{color: plan.color}}>
                        {getMonthlyPrice(plan, billingType, duration)}€
                      </span>
                      <span className="text-[#2A9FCB]/70 text-lg">/mese</span>
                    </div>
                    <p className="text-sm text-[#2A9FCB]/60 mt-1">
                      abbonamento per {duration} {duration === 1 ? 'anno' : 'anni'}
                    </p>
                    {billingType === 'family' && (
                      <p className="text-xs mt-2 px-3 py-1 rounded-full inline-block" style={{background: plan.colorLight, color: plan.color}}>
                        Solo {Math.round(getMonthlyPrice(plan, billingType, duration) / 5)}€/mese a persona
                      </p>
                    )}
                  </div>

                  {/* Features List */}
                  <div className="p-6">
                    <h4 className="font-semibold mb-4 text-[#2A9FCB]">Cosa include:</h4>
                    <ul className="space-y-3">
                      {plan.included.map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <Check className="w-5 h-5 flex-shrink-0 mt-0.5" style={{color: '#10B981'}} />
                          <span className="text-sm text-[#2A9FCB]">{item}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {plan.excluded.length > 0 && (
                      <>
                        <h4 className="font-semibold mt-6 mb-4 text-[#2A9FCB]/70">Non incluso:</h4>
                        <ul className="space-y-2">
                          {plan.excluded.map((item, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <X className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-400" />
                              <span className="text-sm text-[#2A9FCB]/60">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>

                  {/* CTA */}
                  <div className="p-6 pt-0">
                    <button
                      onClick={() => handleSubscribe(key)}
                      className="w-full py-4 rounded-lg font-bold text-white transition-all hover:opacity-90 hover:scale-[1.02] flex items-center justify-center gap-2"
                      style={{background: plan.color}}
                      data-testid={`subscribe-btn-${key}`}
                    >
                      Scegli {plan.name} <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Difference Highlight */}
          <div className="mt-8 text-center p-6 rounded-2xl" style={{background: 'linear-gradient(135deg, #FEF9E7 0%, #FDE68A 100%)'}}>
            <p className="text-lg text-[#2A9FCB]">
              💡 <strong>Con Premium</strong> hai accesso a tutti i trattamenti avanzati, 
              farmaci e colliri inclusi, chirurgia inclusa e medico personale online!
            </p>
          </div>
          
          {/* Nota Primo Anno di Prova */}
          <div className="mt-6 p-5 rounded-xl border-2 border-amber-300" style={{background: '#FFFBEB'}}>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-bold text-amber-700 mb-1">Primo Anno di Prova</h4>
                <p className="text-sm text-amber-800">
                  Durante il <strong>primo anno</strong> di abbonamento sono applicati alcuni limiti: 
                  <strong> non è possibile accedere ai trattamenti avanzati e agli interventi chirurgici</strong>. 
                  Questo periodo serve a costruire insieme il tuo percorso di prevenzione. 
                  Dal secondo anno potrai usufruire di tutti i servizi inclusi nel tuo piano.
                </p>
              </div>
            </div>
          </div>

          {/* Durata Abbonamento */}
          <div className="mt-8 bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-xl font-bold text-center mb-6" style={{color: '#2A9FCB'}}>
              📅 Come Funziona la Durata
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4 rounded-xl" style={{background: '#F0FAF5'}}>
                <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center text-xl font-bold text-white" style={{background: '#2A9FCB'}}>
                  1
                </div>
                <h4 className="font-bold mb-2" style={{color: '#2A9FCB'}}>Primo Anno</h4>
                <p className="text-sm text-[#2A9FCB]/80">
                  Il tuo primo abbonamento ha durata di <strong>1 anno</strong>. Provi il servizio senza vincoli lunghi.
                </p>
              </div>
              <div className="text-center p-4 rounded-xl" style={{background: '#FEF3C7'}}>
                <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center text-xl font-bold text-white" style={{background: '#F59E0B'}}>
                  3
                </div>
                <h4 className="font-bold mb-2" style={{color: '#D97706'}}>Rinnovo 3 Anni</h4>
                <p className="text-sm text-[#2A9FCB]/80">
                  Dopo il primo anno, l'abbonamento si rinnova per <strong>3 anni</strong> per garantirti continuità.
                </p>
              </div>
              <div className="text-center p-4 rounded-xl" style={{background: '#EDE9FE'}}>
                <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center text-xl font-bold text-white" style={{background: '#8B5CF6'}}>
                  5
                </div>
                <h4 className="font-bold mb-2" style={{color: '#6D28D9'}}>Opzione 5 Anni</h4>
                <p className="text-sm text-[#2A9FCB]/80">
                  Per la massima tranquillità, puoi scegliere direttamente il piano <strong>5 anni</strong>.
                </p>
              </div>
            </div>
            <p className="text-center text-sm text-[#2A9FCB]/70 mt-6">
              * I prezzi mensili restano invariati per tutta la durata dell'abbonamento
            </p>
          </div>
        </div>
      </section>

      {/* Savings Calculator */}
      <section className="py-16 px-4" style={{background: '#F8F8F8'}}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg mb-4" style={{background: '#2A9FCB'}}>
              <Calculator className="w-5 h-5 text-white" />
              <span className="font-semibold text-white">Calcolatore Risparmio</span>
            </div>
            <h2 className="text-3xl font-bold mb-2" style={{color: '#2A9FCB'}}>
              Quanto risparmieresti?
            </h2>
            <p className="text-[#2A9FCB]/80">
              Inserisci le tue esigenze annuali e scopri il piano più conveniente per te
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Calculator Inputs */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="font-bold text-lg mb-6" style={{color: '#2A9FCB'}}>
                Di cosa hai bisogno in un anno?
              </h3>
              
              <div className="space-y-5">
                {/* Visita oculistica completa */}
                <div>
                  <label className="flex justify-between text-sm font-medium text-[#2A9FCB] mb-2">
                    <span>👁️ Visite oculistiche complete</span>
                    <span className="text-[#2A9FCB]/70">{calcVisits} × 150€ = {calcVisits * 150}€</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="6"
                    value={calcVisits}
                    onChange={(e) => setCalcVisits(parseInt(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{background: `linear-gradient(to right, #2A9FCB ${calcVisits * 100 / 6}%, #E5E7EB ${calcVisits * 100 / 6}%)`}}
                  />
                  <div className="flex justify-between text-xs text-[#2A9FCB]/60 mt-1">
                    <span>0</span><span>6</span>
                  </div>
                </div>

                {/* Visite di controllo */}
                <div>
                  <label className="flex justify-between text-sm font-medium text-[#2A9FCB] mb-2">
                    <span>🔍 Visite di controllo</span>
                    <span className="text-[#2A9FCB]/70">{calcControls} × 100€ = {calcControls * 100}€</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="6"
                    value={calcControls}
                    onChange={(e) => setCalcControls(parseInt(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{background: `linear-gradient(to right, #2A9FCB ${calcControls * 100 / 6}%, #E5E7EB ${calcControls * 100 / 6}%)`}}
                  />
                  <div className="flex justify-between text-xs text-[#2A9FCB]/60 mt-1">
                    <span>0</span><span>6</span>
                  </div>
                </div>

                {/* OCT */}
                <div>
                  <label className="flex justify-between text-sm font-medium text-[#2A9FCB] mb-2">
                    <span>📊 Esami OCT</span>
                    <span className="text-[#2A9FCB]/70">{calcOCT} × 100€ = {calcOCT * 100}€</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="4"
                    value={calcOCT}
                    onChange={(e) => setCalcOCT(parseInt(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{background: `linear-gradient(to right, #2A9FCB ${calcOCT * 100 / 4}%, #E5E7EB ${calcOCT * 100 / 4}%)`}}
                  />
                  <div className="flex justify-between text-xs text-[#2A9FCB]/60 mt-1">
                    <span>0</span><span>4</span>
                  </div>
                </div>

                {/* Campo visivo */}
                <div>
                  <label className="flex justify-between text-sm font-medium text-[#2A9FCB] mb-2">
                    <span>👀 Campo visivo</span>
                    <span className="text-[#2A9FCB]/70">{calcCampoVisivo} × 90€ = {calcCampoVisivo * 90}€</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="4"
                    value={calcCampoVisivo}
                    onChange={(e) => setCalcCampoVisivo(parseInt(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{background: `linear-gradient(to right, #2A9FCB ${calcCampoVisivo * 100 / 4}%, #E5E7EB ${calcCampoVisivo * 100 / 4}%)`}}
                  />
                  <div className="flex justify-between text-xs text-[#2A9FCB]/60 mt-1">
                    <span>0</span><span>4</span>
                  </div>
                </div>

                {/* Topografia corneale */}
                <div>
                  <label className="flex justify-between text-sm font-medium text-[#2A9FCB] mb-2">
                    <span>🗺️ Topografia corneale</span>
                    <span className="text-[#2A9FCB]/70">{calcTopografia} × 90€ = {calcTopografia * 90}€</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="4"
                    value={calcTopografia}
                    onChange={(e) => setCalcTopografia(parseInt(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{background: `linear-gradient(to right, #2A9FCB ${calcTopografia * 100 / 4}%, #E5E7EB ${calcTopografia * 100 / 4}%)`}}
                  />
                  <div className="flex justify-between text-xs text-[#2A9FCB]/60 mt-1">
                    <span>0</span><span>4</span>
                  </div>
                </div>

                {/* Tonometria */}
                <div>
                  <label className="flex justify-between text-sm font-medium text-[#2A9FCB] mb-2">
                    <span>📏 Tonometria</span>
                    <span className="text-[#2A9FCB]/70">{calcTonometria} × 50€ = {calcTonometria * 50}€</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="4"
                    value={calcTonometria}
                    onChange={(e) => setCalcTonometria(parseInt(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{background: `linear-gradient(to right, #2A9FCB ${calcTonometria * 100 / 4}%, #E5E7EB ${calcTonometria * 100 / 4}%)`}}
                  />
                  <div className="flex justify-between text-xs text-[#2A9FCB]/60 mt-1">
                    <span>0</span><span>4</span>
                  </div>
                </div>

                {/* Retinografia */}
                <div>
                  <label className="flex justify-between text-sm font-medium text-[#2A9FCB] mb-2">
                    <span>📸 Retinografia</span>
                    <span className="text-[#2A9FCB]/70">{calcRetinografia} × 100€ = {calcRetinografia * 100}€</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="4"
                    value={calcRetinografia}
                    onChange={(e) => setCalcRetinografia(parseInt(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{background: `linear-gradient(to right, #2A9FCB ${calcRetinografia * 100 / 4}%, #E5E7EB ${calcRetinografia * 100 / 4}%)`}}
                  />
                  <div className="flex justify-between text-xs text-[#2A9FCB]/60 mt-1">
                    <span>0</span><span>4</span>
                  </div>
                </div>

                {/* Ecografia bulbare */}
                <div>
                  <label className="flex justify-between text-sm font-medium text-[#2A9FCB] mb-2">
                    <span>🔬 Ecografia bulbare</span>
                    <span className="text-[#2A9FCB]/70">{calcEcografia} × 120€ = {calcEcografia * 120}€</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="4"
                    value={calcEcografia}
                    onChange={(e) => setCalcEcografia(parseInt(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{background: `linear-gradient(to right, #2A9FCB ${calcEcografia * 100 / 4}%, #E5E7EB ${calcEcografia * 100 / 4}%)`}}
                  />
                  <div className="flex justify-between text-xs text-[#2A9FCB]/60 mt-1">
                    <span>0</span><span>4</span>
                  </div>
                </div>

                {/* Chirurgia */}
                <div className="pt-2">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={calcChirurgia}
                      onChange={(e) => setCalcChirurgia(e.target.checked)}
                      className="w-5 h-5 rounded"
                      style={{accentColor: '#2A9FCB'}}
                    />
                    <span className="text-sm font-medium text-[#2A9FCB]">
                      ⚕️ Intervento chirurgico bilaterale <span className="text-[#2A9FCB]/70">+3.000€</span>
                    </span>
                  </label>
                  <p className="text-xs text-[#2A9FCB]/60 ml-8 mt-1">
                    (strabismo, cataratta, crosslinking, chirurgia refrattiva)
                  </p>
                </div>
              </div>

              {/* Single Cost Total */}
              <div className="mt-6 p-4 rounded-xl" style={{background: '#FEF3C7'}}>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-[#2A9FCB]">💸 Costo senza abbonamento:</span>
                  <span className="text-2xl font-bold" style={{color: '#D97706'}}>{singleCost}€/anno</span>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="font-bold text-lg mb-6 text-center" style={{color: '#2A9FCB'}}>
                Il tuo risparmio con i nostri piani:
              </h3>

              <div className="grid gap-4">
                {Object.entries(PLANS).map(([key, plan]) => {
                  const savings = calculateSavings(key);
                  const isPositive = savings.savings > 0;
                  const Icon = plan.icon;
                  const monthlyPrice = getMonthlyPrice(plan, billingType, duration);
                  
                  return (
                    <div 
                      key={key}
                      className={`p-5 rounded-xl border-2 transition-all ${plan.recommended ? 'shadow-lg' : ''}`}
                      style={{
                        borderColor: isPositive ? '#10B981' : '#E5E7EB',
                        background: plan.recommended ? plan.colorLight : 'white'
                      }}
                    >
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{background: plan.color}}>
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-bold flex items-center gap-2" style={{color: plan.color}}>
                              {plan.name}
                              {plan.recommended && <span className="text-xs px-2 py-0.5 rounded-full bg-white">CONSIGLIATO</span>}
                            </h4>
                            <p className="text-sm text-[#2A9FCB]/70">{monthlyPrice}€/mese</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {isPositive ? (
                            <>
                              <p className="text-2xl font-bold" style={{color: '#10B981'}}>
                                -{savings.savings}€
                              </p>
                              <p className="text-sm" style={{color: '#10B981'}}>
                                Risparmi il {savings.percentage}%
                              </p>
                            </>
                          ) : (
                            <>
                              <p className="text-lg font-medium" style={{color: savings.savings === 0 ? '#2A9FCB' : '#EF4444'}}>
                                {savings.savings === 0 ? 'Pari' : `+${Math.abs(savings.savings)}€`}
                              </p>
                              {savings.extraCost > 0 && (
                                <p className="text-xs text-red-500">
                                  (chirurgia non inclusa)
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      
                      {plan.recommended && isPositive && (
                        <button
                          onClick={() => handleSubscribe(key)}
                          className="w-full mt-4 py-3 rounded-lg font-bold text-white flex items-center justify-center gap-2"
                          style={{background: plan.color}}
                        >
                          Scegli Premium e risparmia {savings.savings}€ <ArrowRight className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}

              <p className="text-xs text-[#2A9FCB]/60 text-center mt-4">
                * Il calcolo è indicativo e basato sui prezzi del listino ufficiale
              </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Full Comparison Table */}
      <section className="py-16 px-4" style={{background: '#F9FAFB'}}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8" style={{color: '#2A9FCB'}}>
            Confronto Dettagliato
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-2xl shadow-lg overflow-hidden">
              <thead>
                <tr style={{background: '#F9FAFB'}}>
                  <th className="text-left p-4 font-semibold text-[#2A9FCB]/80 w-1/3">Caratteristica</th>
                  {Object.entries(PLANS).map(([key, plan]) => (
                    <th key={key} className="p-4 text-center" style={{background: plan.recommended ? plan.colorLight : ''}}>
                      <div className="font-bold text-lg" style={{color: plan.color}}>{plan.name}</div>
                      <div className="text-sm text-[#2A9FCB]/70">{getMonthlyPrice(plan, billingType, duration)}€/mese</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_FEATURES.map((feature, idx) => (
                  <tr key={feature.key} className={idx % 2 === 0 ? 'bg-gray-50' : ''}>
                    <td className="p-4 text-sm text-[#2A9FCB]">{feature.label}</td>
                    {Object.entries(PLANS).map(([key, plan]) => (
                      <td 
                        key={key} 
                        className="p-4 text-center"
                        style={{background: plan.recommended && idx % 2 === 0 ? 'rgba(245, 158, 11, 0.05)' : plan.recommended ? 'rgba(245, 158, 11, 0.1)' : ''}}
                      >
                        {renderFeatureValue(feature, plan.features[feature.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Why Choose Subscription */}
      <section className="py-16 px-4" style={{background: 'white'}}>
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4" style={{color: '#2A9FCB'}}>
            Perché scegliere un abbonamento?
          </h2>
          <p className="text-[#2A9FCB]/80 mb-12 max-w-2xl mx-auto">
            Una singola visita completa costa <strong>150€</strong>. Con un abbonamento ottieni molto di più.
          </p>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Eye, title: 'Risparmi nel tempo', desc: 'Costi prevedibili e inferiori' },
              { icon: Heart, title: 'Continuità di cura', desc: 'Un team che ti conosce' },
              { icon: Clock, title: 'Zero attese', desc: 'Prenotazione prioritaria' },
              { icon: Phone, title: 'Sempre connesso', desc: 'Telemedicina e assistenza H24' }
            ].map((item, idx) => (
              <div key={idx} className="p-6 rounded-xl" style={{background: '#F0FAF5'}}>
                <item.icon className="w-10 h-10 mx-auto mb-3" style={{color: '#2A9FCB'}} />
                <h3 className="font-bold mb-1" style={{color: '#2A9FCB'}}>{item.title}</h3>
                <p className="text-sm text-[#2A9FCB]/80">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4" style={{color: '#2c4667'}}>
            Hai dubbi? Ti aiutiamo noi!
          </h2>
          <p className="text-xl mb-8" style={{color: '#2c4667'}}>
            Il nostro staff ti guiderà nella scelta del piano perfetto per te
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button 
              onClick={() => navigate('/booking')}
              className="px-8 py-4 rounded-xl font-bold text-lg transition-all hover:scale-105"
              style={{background: '#FA7314', color: 'white'}}
            >
              Prenota una Consulenza Gratuita
            </button>
            <button 
              onClick={() => navigate('/abbonamenti/faq')}
              className="px-8 py-4 rounded-xl font-bold text-lg border-2 transition-all hover:opacity-80"
              style={{borderColor: '#2c4667', color: '#2c4667'}}
            >
              Leggi le FAQ
            </button>
            <button 
              onClick={() => navigate('/listino-prezzi')}
              className="px-8 py-4 rounded-xl font-bold text-lg border-2 transition-all hover:opacity-80"
              style={{borderColor: '#2c4667', color: '#2c4667'}}
            >
              📋 Listino Prezzi
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Abbonamenti;
